import React, {useMemo, useState, useEffect} from 'react';
import {Route, useHistory} from 'react-router-dom';
import fp from 'lodash/fp';

import {useSelector, useDispatch} from 'react-redux';

import {
    useStorage, debounce, generateRandomString, pkceChallengeFromVerifier
} from 'lib/util';
import {
    receiveData, userinfo, directory, login, logout,
    selectDirectory, selectAccessToken,
} from 'aauth/ducks/jsonapi'; 
import {url_for} from 'aauth/components/Router';

class Api {
    constructor({
        root, client_id, dispatch, history, directory,
        accessToken, refreshToken, setRefreshToken,
        pkceCodeVerifier, pkceCodeChallenge,
    }) {
        this._root = root;
        this._client_id = client_id;
        this._dispatch = dispatch;
        this._history = history;
        this.directory = directory;
        this._accessToken = accessToken;
        this._refreshToken = refreshToken;
        this._setRefreshToken = setRefreshToken;
        this._pkceCodeVerifier = pkceCodeVerifier;
        this._pkceCodeChallenge = pkceCodeChallenge;
    }
    
    bootstrap = debounce(async () => {
        if(!this.directory) {
            const resp = await this.fetch(this._root);
            const data = await resp.json();
            this._dispatch(directory(data));
            this.directory = data;
        }
        if(!this.isAuthorized() && this._refreshToken) {
            await this.login({
                grant_type: 'refresh_token',
                refresh_token: this._refreshToken
            });
        }
    })

    isAuthorized = () => {
        return !!this._accessToken;
    }

    hasRefreshToken = () => {
        return !!this._refreshToken;
    }

    fetch = async (url, options={}) => {
        let {json, headers={}, ...o} = options;
        if(json) {
            o.body = JSON.stringify(json);
            headers['Content-Type'] = 'application/json';
        }
        if(this._accessToken) {
            await this._refreshIfNeeded();
            headers['Authorization'] = `Bearer ${this._accessToken.data}`
        }
        o.headers = headers;
        let resp = await fetch(url, o);
        if(!resp.ok) {
            let err = new Error(`Bad response ${resp}`);
            err.response = resp;
            throw err;
        }
        return resp;
    }

    login = async args => {
        var data;
        try {
            // Don't use this.fetch b/c it tries to refresh the token
            let resp = await fetch(
                this.directory.data.links['oauth.token'],
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(args)
                })
            if(!resp.ok) {
                let err = new Error(`Bad response to login ${resp}`);
                err.response = resp;
                throw err;
            }

            data = await resp.json();
            this._processTokenData(data);
            resp = await this.fetch(this.directory.data.links['oauth.userinfo'])
            data = await resp.json();
            this._dispatch(userinfo(data));
        } catch(e) {
            console.error('Error logging in', args);
            this._setRefreshToken(null);
            this._accessToken = null;
            this._dispatch(logout());
        }
        return data;
    }

    logout = async revoke_all => {
        if(!this._accessToken) {
            return
        }
        let json = revoke_all ? {revoke_all: true} : {
            tokens: [this._accessToken.data, this._refreshToken]
        }
        await this.fetch(
            this.directory.data.links['oauth.revoke'],
            {method: 'POST', json}
        );
        this._setRefreshToken(null);
        this._accessToken = null;
        this._dispatch(logout());
    }

    isAuthorized = () => !!this._accessToken

    fetchJson = async (url, options={}) => {
        let resp = await(this.fetch(url, options));
        if(!resp.ok) {
            let e = new Error('Fetch error');
            e.resp = resp;
            throw(e);
        }
        if(resp.status === 204) {
            return null;
        } else {
            let json = await resp.json();
            this._dispatch(receiveData(json));
            return json;
        }
    }

    authorizeLink = (base, options) => {
        let {intent, redirect_uri} = options;
        let uri = new URL(base);
        uri.searchParams.set('redirect_uri', redirect_uri);
        uri.searchParams.set('intent', intent);
        uri.searchParams.set('code_challenge', this._pkceCodeChallenge);
        uri.searchParams.set('code_challenge_method', 'S256');
        return uri.toString();
    }

    codeLogin = async code => {
        let options = {
            grant_type: 'authorization_code',
            code, 
            client_id: this._client_id,
            code_verifier: this._pkceCodeVerifier
        };
        return await this.login(options);
    }

    codeLink = async code => {
        let resp = await this.fetchJson(
            this.directory.data.links['oauth.link'],
            {
                method: 'POST',
                json: {code, client_id: this._client_id, code_verifier: this._pkceCodeVerifier}
            }
        );
    }

    _refreshIfNeeded = async () => {
        if(fp.isEmpty(this._accessToken)) return;
        const {exp} = this._accessToken;
        const now = (new Date()).getTime();
        if(exp - now > 5000) return;
        await this.tryRefresh();
    }

    tryRefresh = debounce(async() => {
        if(this._refreshToken) {
            await this.login({
                grant_type: 'refresh_token',
                refresh_token: this._refreshToken
            });
            return true;
        }
        return false;
    });

    _processTokenData = data => {
        const now = new Date();
        const exp = now.getTime() + 1000 * data['expires_in'];
        this._accessToken = {
            data: data['access_token'],
            exp
        }
        this._dispatch(login(this._accessToken));
        if(data.refresh_token) {
            this._setRefreshToken(data.refresh_token);
        }
    }

}

const Context = React.createContext();

export const useApi = () => React.useContext(Context);

export const ApiRoute = ({authorized, ...props}) => {
    const api = useApi();
    const history = useHistory();
    const [init, setInit] = useState(false);
    const [ready, setReady] = useState(false);
    
    useEffect(() => {
        const boot = async () => {
            await api.bootstrap();
            console.log('Bootstrapped. api.isAuthorized()', api.isAuthorized());
            if(authorized && !api.isAuthorized()) {
                history.push(url_for('home'))
            }
            setReady(true);
        }
        if(!init) {
            setInit(true);
            boot();
        }
    }, [init, api, authorized, history]);
    return (ready ? <Route {...props}/> : null);
}


export default function Provider({root, client_id, children}) {
    const dispatch = useDispatch();
    const history = useHistory();
    const directory = useSelector(selectDirectory);
    const accessToken = useSelector(selectAccessToken);
    const [refreshToken, setRefreshToken] = useStorage(sessionStorage, 'aauth.refresh_token');
    const [pkceCodeVerifier, setPkceCodeVerifier] = useStorage(sessionStorage, 'aauth.pkce_code_verifier');
    const [pkceCodeChallenge, setPkceCodeChallenge] = useState();
    
    useEffect(() => {
        if(!pkceCodeVerifier) {
            setPkceCodeVerifier(generateRandomString(33));
        }
    }, [pkceCodeVerifier, setPkceCodeVerifier, setPkceCodeChallenge]);

    useEffect(() => {
        pkceChallengeFromVerifier(pkceCodeVerifier).then(
            setPkceCodeChallenge
        );
    }, [pkceCodeVerifier, setPkceCodeChallenge]);

    const api = useMemo(() => new Api({
        root, client_id, dispatch, history, directory, accessToken, 
        refreshToken, setRefreshToken,
        pkceCodeVerifier, pkceCodeChallenge,
    }), [
        root, dispatch, history, directory, 
        accessToken, refreshToken, setRefreshToken,
        pkceCodeVerifier, pkceCodeChallenge,
    ]);

    return <Context.Provider value={api}>{children}</Context.Provider>
}



